import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { commonTextFieldProps, smallButtonProp } from "../../../utils/styles";
import { formatDate, formatCurrency } from "../../../utils/utils";
import { financeApi } from "../apiService";
import { useToast } from "../../../components/Toast";

const OpeningBalanceModal = ({
  open,
  onClose,
  selectedDate,
  getCashBook,
}: {
  open: boolean;
  onClose: () => void;
  selectedDate: string;
  getCashBook: () => void;
}) => {
  const [openingBalance, setOpeningBalance] = useState(0);
  const [totalCashBalance, setTotalCashBalance] = useState(0);
  const [totalOnlineBalance, setTotalOnlineBalance] = useState(0);
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate opening balance whenever cash or online balance changes
  useEffect(() => {
    const calculatedBalance = totalCashBalance + totalOnlineBalance;
    setOpeningBalance(calculatedBalance);
  }, [totalCashBalance, totalOnlineBalance]);

  // Get yesterday's date for display and submission
  const yesterday = new Date(selectedDate);
  yesterday.setDate(yesterday.getDate() - 1);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const response = await financeApi.createCashBook(
        yesterday.toISOString().split("T")[0],
        0,
        0,
        openingBalance,
        totalCashBalance,
        totalOnlineBalance
      );
      if (response) {
        onClose();
        showToast("Opening Balance created successfully", "success");
        getCashBook();
      } else {
        showToast("Failed to create opening balance", "error");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          mt: 4, // margin from top (adjust as needed)
          alignSelf: "flex-start", // stick to the top of the container
        },
      }}
    >
      <DialogTitle sx={{ fontSize: "1rem" }}>Opening Balance</DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, fontStyle: "italic" }}
        >
          Please enter the opening balance amount on {formatDate(selectedDate)}{" "}
          for your cashbook, or lock the previous day's cashbook to maintain
          accurate financial records.
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            mb: 1,
          }}
        >
          <TextField
            label="Total Cash Balance"
            value={totalCashBalance}
            onChange={(e) => setTotalCashBalance(Number(e.target.value))}
            {...commonTextFieldProps}
          />
          <TextField
            label="Total Bank Balance"
            value={totalOnlineBalance}
            onChange={(e) => setTotalOnlineBalance(Number(e.target.value))}
            {...commonTextFieldProps}
          />
          <Typography
            variant="body1"
            sx={{
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              fontSize: "0.8rem",
            }}
          >
            Opening Balance: {formatCurrency(openingBalance.toString())}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mb: 2,
            maxWidth: "20%",
          }}
        ></Box>
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button
            variant="text"
            color="primary"
            onClick={onClose}
            {...smallButtonProp}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            {...smallButtonProp}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default OpeningBalanceModal;
